import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useT } from "@/i18n";
import {
  ShieldCheck,
  BadgeCheck,
  BookOpen,
  LineChart,
  PiggyBank,
  Landmark,
  Wallet,
  Users,
  Quote,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const IMG = {
  hero: "/images/hero.jpg",
  family: "/images/family.jpg",
  advisor1: "/images/advisor1.jpg",
  advisor2: "/images/advisor2.jpg",
  advisor3: "/images/advisor3.jpg",
  flag: "/images/flag.jpg",
};

const Home = () => {
  const { t } = useT();
  const navigate = useNavigate();

  const [form, setForm] = useState({ name: "", email: "", consent: false });
  const [error, setError] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setError(t("newsletter.error_required"));
      return;
    }
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
    if (!emailOk) {
      setError(t("newsletter.error_email"));
      return;
    }
    if (!form.consent) {
      setError(t("newsletter.error_consent"));
      return;
    }
    setError("");
    navigate("/thank_you");
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-200 bg-white">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8 lg:py-24">
          <div>
            <p
              data-testid="hero-eyebrow"
              className="text-xs font-semibold uppercase tracking-widest text-[#0F766E]"
            >
              {t("hero.eyebrow")}
            </p>
            <h1
              data-testid="hero-title"
              className="mt-4 font-serif text-4xl font-bold leading-tight tracking-tight text-slate-900 sm:text-5xl lg:text-6xl"
            >
              {t("hero.title")}
            </h1>
            <p
              data-testid="hero-subtitle"
              className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
            >
              {t("hero.subtitle")}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button
                asChild
                data-testid="hero-cta-primary"
                className="h-12 rounded-md bg-[#0B2545] px-6 text-sm font-semibold text-white hover:bg-[#133B6A]"
              >
                <a href="#contact">
                  {t("hero.cta_primary")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                data-testid="hero-cta-secondary"
                className="h-12 rounded-md border-slate-300 px-6 text-sm font-semibold text-slate-700 hover:border-[#0B2545] hover:text-[#0B2545]"
              >
                <a href="#programs">{t("hero.cta_secondary")}</a>
              </Button>
            </div>

            <div className="mt-8 flex items-center gap-3 text-sm text-slate-600">
              <ShieldCheck className="h-5 w-5 text-[#16A34A]" />
              <span data-testid="hero-trust-badge">{t("hero.trust_badge")}</span>
            </div>
          </div>

          <div className="relative">
            <img
              src={IMG.hero}
              alt={t("hero.image_alt")}
              data-testid="hero-image"
              className="h-full max-h-[560px] w-full rounded-2xl object-cover shadow-xl"
              loading="eager"
            />
            <div className="absolute -bottom-6 -left-6 hidden overflow-hidden rounded-xl border border-slate-200 bg-white p-4 shadow-lg lg:block">
              <div className="flex items-center gap-3">
                <img
                  src={IMG.flag}
                  alt=""
                  className="h-12 w-12 rounded-md object-cover"
                />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-[#0F766E]">
                    2026
                  </p>
                  <p className="font-serif text-sm font-semibold text-slate-900">
                    {t("brand.tagline")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-8 px-4 py-12 sm:px-6 lg:grid-cols-4 lg:px-8">
          {[
            { v: "12,480+", k: "families", Icon: Users },
            { v: "38", k: "advisors", Icon: BadgeCheck },
            { v: "72", k: "programs", Icon: Landmark },
            { v: "97%", k: "satisfaction", Icon: LineChart },
          ].map(({ v, k, Icon }) => (
            <div
              key={k}
              data-testid={`stat-${k}`}
              className="flex flex-col items-start"
            >
              <Icon className="h-6 w-6 text-[#0D9488]" />
              <p className="mt-3 font-serif text-3xl font-bold text-slate-900">
                {v}
              </p>
              <p className="mt-1 text-sm text-slate-600">
                {t(`stats.${k}`)}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ELIGIBILITY / PROGRAMS */}
      <section id="programs" className="py-20">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:items-center lg:px-8">
          <div className="order-2 lg:order-1">
            <img
              src={IMG.family}
              alt={t("eligibility.image_alt")}
              data-testid="eligibility-image"
              className="h-full w-full rounded-2xl object-cover shadow-lg"
              loading="lazy"
            />
          </div>
          <div className="order-1 lg:order-2">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#0F766E]">
              {t("eligibility.eyebrow")}
            </p>
            <h2
              data-testid="eligibility-title"
              className="mt-3 font-serif text-3xl font-bold text-slate-900 sm:text-4xl"
            >
              {t("eligibility.title")}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              {t("eligibility.description")}
            </p>
            <ul className="mt-8 space-y-6">
              {[1, 2, 3].map((n) => (
                <li key={n} className="flex gap-4">
                  <div className="mt-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0B2545] font-serif text-sm font-bold text-white">
                    {n}
                  </div>
                  <div>
                    <h3 className="font-serif text-lg font-semibold text-slate-900">
                      {t(`eligibility.point_${n}_title`)}
                    </h3>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">
                      {t(`eligibility.point_${n}_body`)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* EDUCATION */}
      <section id="education" className="border-y border-slate-200 bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#0F766E]">
              {t("education.eyebrow")}
            </p>
            <h2
              data-testid="education-title"
              className="mt-3 font-serif text-3xl font-bold text-slate-900 sm:text-4xl"
            >
              {t("education.title")}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              {t("education.description")}
            </p>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { k: 1, Icon: Wallet },
              { k: 2, Icon: LineChart },
              { k: 3, Icon: PiggyBank },
              { k: 4, Icon: BookOpen },
            ].map(({ k, Icon }) => (
              <Card
                key={k}
                data-testid={`education-card-${k}`}
                className="border-slate-200 bg-white shadow-none transition-colors hover:border-[#0D9488]"
              >
                <CardHeader className="pb-2">
                  <Icon className="h-6 w-6 text-[#0D9488]" />
                  <CardTitle className="mt-3 font-serif text-lg text-slate-900">
                    {t(`education.card_${k}_title`)}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm leading-relaxed text-slate-600">
                    {t(`education.card_${k}_body`)}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ADVISORS */}
      <section id="advisors" className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#0F766E]">
              {t("advisors.eyebrow")}
            </p>
            <h2
              data-testid="advisors-title"
              className="mt-3 font-serif text-3xl font-bold text-slate-900 sm:text-4xl"
            >
              {t("advisors.title")}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              {t("advisors.description")}
            </p>
          </div>

          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {[1, 2, 3].map((k) => (
              <article
                key={k}
                data-testid={`advisor-card-${k}`}
                className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
              >
                <div className="flex items-center gap-3">
                  <div
                    aria-hidden="true"
                    className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#0B2545] font-serif text-lg font-bold text-white"
                  >
                    {t(`advisors.advisor_${k}_name`)
                      .split(" ")
                      .filter((w) => /^[A-ZÁÉÍÓÚÑ]/.test(w))
                      .slice(0, 2)
                      .map((w) => w[0])
                      .join("")}
                  </div>
                  <div>
                    <h3 className="font-serif text-lg font-semibold text-slate-900">
                      {t(`advisors.advisor_${k}_name`)}
                    </h3>
                    <p className="mt-1 text-xs font-semibold uppercase tracking-widest text-[#0F766E]">
                      {t(`advisors.advisor_${k}_role`)}
                    </p>
                  </div>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-600">
                  {t(`advisors.advisor_${k}_bio`)}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="border-y border-slate-200 bg-[#F1F5F9] py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#0F766E]">
              {t("testimonials.eyebrow")}
            </p>
            <h2
              data-testid="testimonials-title"
              className="mt-3 font-serif text-3xl font-bold text-slate-900 sm:text-4xl"
            >
              {t("testimonials.title")}
            </h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {[1, 2, 3].map((n) => (
              <figure
                key={n}
                data-testid={`testimonial-${n}`}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
              >
                <Quote className="h-6 w-6 text-[#0D9488]" />
                <blockquote className="mt-4 text-sm leading-relaxed text-slate-700">
                  “{t(`testimonials.t${n}_quote`)}”
                </blockquote>
                <figcaption className="mt-6 border-t border-slate-100 pt-4">
                  <p className="font-serif text-sm font-semibold text-slate-900">
                    {t(`testimonials.t${n}_name`)}
                  </p>
                  <p className="text-xs text-slate-500">
                    {t(`testimonials.t${n}_city`)}
                  </p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER / CONTACT (form) */}
      <section id="contact" className="py-20">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:items-center lg:px-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-[#0F766E]">
              {t("newsletter.eyebrow")}
            </p>
            <h2
              data-testid="newsletter-title"
              className="mt-3 font-serif text-3xl font-bold text-slate-900 sm:text-4xl"
            >
              {t("newsletter.title")}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              {t("newsletter.description")}
            </p>
            <p className="mt-6 text-xs text-slate-500">{t("newsletter.note")}</p>
          </div>

          <form
            onSubmit={submit}
            data-testid="newsletter-form"
            noValidate
            className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
          >
            <div className="space-y-4">
              <div>
                <Label
                  htmlFor="newsletter-name"
                  className="text-sm font-medium text-slate-800"
                >
                  {t("newsletter.name_label")}
                </Label>
                <Input
                  id="newsletter-name"
                  data-testid="newsletter-name-input"
                  type="text"
                  value={form.name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, name: e.target.value }))
                  }
                  placeholder={t("newsletter.name_placeholder")}
                  className="mt-2 h-11"
                  autoComplete="name"
                />
              </div>
              <div>
                <Label
                  htmlFor="newsletter-email"
                  className="text-sm font-medium text-slate-800"
                >
                  {t("newsletter.email_label")}
                </Label>
                <Input
                  id="newsletter-email"
                  data-testid="newsletter-email-input"
                  type="email"
                  value={form.email}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, email: e.target.value }))
                  }
                  placeholder={t("newsletter.email_placeholder")}
                  className="mt-2 h-11"
                  autoComplete="email"
                />
              </div>

              <div className="flex items-start gap-2">
                <Checkbox
                  id="newsletter-consent"
                  data-testid="newsletter-consent-checkbox"
                  checked={form.consent}
                  onCheckedChange={(v) =>
                    setForm((f) => ({ ...f, consent: v === true }))
                  }
                  className="mt-0.5"
                />
                <Label
                  htmlFor="newsletter-consent"
                  className="text-xs leading-relaxed text-slate-600"
                >
                  {t("newsletter.consent")}
                </Label>
              </div>

              {error ? (
                <p
                  data-testid="newsletter-error"
                  className="text-sm font-medium text-red-600"
                >
                  {error}
                </p>
              ) : null}

              <Button
                type="submit"
                data-testid="newsletter-submit"
                className="h-12 w-full rounded-md bg-[#0B2545] text-sm font-semibold text-white hover:bg-[#133B6A]"
              >
                {t("newsletter.submit")}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Home;
